import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useActor } from '../hooks/useActor';
import { useInternetIdentity } from '../hooks/useInternetIdentity';
import { WalletType as BackendWalletType } from '../backend';
import {
  connectWeb3Wallet,
  handleWalletError,
  fetchWeb3Balance,
  fetchRavencoinBalance,
  getNetworkInfo,
  connectRavencoinCore,
} from '../lib/walletConnectors';
import {
  connectICPWallet,
  disconnectICPWallet,
  getICPPrincipal,
} from '../lib/icpWalletConnector';
import {
  connectSolanaWallet,
  disconnectSolanaWallet,
  fetchSolanaBalance,
} from '../lib/solanaWalletConnector';
import {
  connectXRPWallet,
  disconnectXRPWallet,
  fetchXRPBalance,
} from '../lib/xrpWalletConnector';
import {
  disconnectWalletConnect,
  onWalletConnectAccountsChanged,
  onWalletConnectChainChanged,
  onWalletConnectDisconnect,
  onWalletConnectConnect,
  getWalletConnectProvider,
  isWalletConnectConnected,
} from '../lib/walletConnect';
import { toast } from 'sonner';
// Note: useGetCustomTokenPairs is dynamically imported when needed to avoid circular dependencies
import { NETWORK_TO_CHAIN_ID } from '../lib/tokenRegistry';

export type WalletType = 'web3' | 'ravencoin' | 'icp' | 'solana' | 'xrp' | null;

interface WalletContextType {
  isConnected: boolean;
  walletAddress: string | null;
  walletType: WalletType;
  connect: (type: WalletType) => Promise<void>;
  disconnect: () => void;
  balances: Record<string, number>;
  isConnecting: boolean;
  connectionError: string | null;
  networkInfo: { chainId: string; networkName: string } | null;
  refreshBalances: () => Promise<void>;
  isICReady: boolean;
  icInitMessage: string | null;
  showReconnectionPrompt: boolean;
  confirmReconnection: () => void;
  dismissReconnectionPrompt: () => void;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean;
      isTrust?: boolean;
      request: (args: { method: string; params?: any[] }) => Promise<any>;
      on?: (event: string, callback: (...args: any[]) => void) => void;
      removeListener?: (event: string, callback: (...args: any[]) => void) => void;
    };
  }
}

// Helper to safely extract error messages - prevents binary data from being displayed
function getSafeErrorMessage(error: any, fallback: string = 'An error occurred'): string {
  if (!error) return fallback;
  
  // If error.message exists and is a reasonable string, use it
  if (error.message && typeof error.message === 'string') {
    // Check if it looks like binary data (long string of numbers/commas)
    if (error.message.length > 200 || /^[\d,\s\[\]"]+$/.test(error.message)) {
      return fallback;
    }
    return error.message;
  }
  
  // If error is a string itself
  if (typeof error === 'string') {
    if (error.length > 200 || /^[\d,\s\[\]"]+$/.test(error)) {
      return fallback;
    }
    return error;
  }
  
  return fallback;
}

function toBackendWalletType(type: WalletType): BackendWalletType {
  switch (type) {
    case 'web3':
      return { web3: null };
    case 'ravencoin':
      return { ravencoinCore: null };
    case 'icp':
      return { internetIdentity: null };
    case 'solana':
      return { web3: null }; // Treat as web3 for backend (address-based)
    case 'xrp':
      return { web3: null }; // Treat as web3 for backend
    default:
      return { web3: null };
  }
}

function isValidRavencoinAddress(address: string): boolean {
  if (!address || address.length !== 34 || !address.startsWith('R')) {
    return false;
  }

  const placeholderPatterns = ['RVN_PLACEHOLDER', 'RVN_PROXY', /^R[X]+$/, /^R[0]+$/];

  for (const pattern of placeholderPatterns) {
    if (typeof pattern === 'string') {
      if (address.includes(pattern)) {
        return false;
      }
    } else if (pattern.test(address)) {
      return false;
    }
  }

  return true;
}

export function WalletProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  
  // Localhost development bypass for testing without wallet connection
  const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || 
     window.location.hostname === '127.0.0.1' ||
     window.location.hostname === '');
  
  const devBypassEnabled = isLocalhost && 
    (import.meta.env.DEV || localStorage.getItem('kawswap_dev_bypass') === 'true');
  
  const [isConnected, setIsConnected] = useState(devBypassEnabled);
  const [walletAddress, setWalletAddress] = useState<string | null>(
    devBypassEnabled ? '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb' : null
  );
  const [walletType, setWalletType] = useState<WalletType>(devBypassEnabled ? 'web3' : null);
  const [balances, setBalances] = useState<Record<string, number>>(
    devBypassEnabled ? { 
      RVN: 1000, 
      BTC: 0.5, 
      ETH: 10, 
      USDT: 5000, 
      USDC: 3000,
      LTC: 50,
      DOGE: 10000
    } : {}
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const refreshAdminStatus = () => {
    // Invalidate and immediately set to false to clear cached admin status
    queryClient.setQueryData(['isCallerAdmin'], false);
    queryClient.setQueryData(['isCallerTemporaryAdmin'], false);
    queryClient.invalidateQueries({ queryKey: ['isCallerAdmin'] });
    queryClient.invalidateQueries({ queryKey: ['isCallerTemporaryAdmin'] });
    // Force refetch to ensure backend state is checked
    queryClient.refetchQueries({ queryKey: ['isCallerAdmin'] });
    queryClient.refetchQueries({ queryKey: ['isCallerTemporaryAdmin'] });
  };

  const setAdminStatus = (value: boolean) => {
    queryClient.setQueryData(['isCallerAdmin'], value);
  };

  // Clear admin status immediately on mount if no wallet is stored
  useEffect(() => {
    const stored = localStorage.getItem('kawswap_wallet');
    if (!stored) {
      // No stored wallet - clear admin status immediately
      setAdminStatus(false);
      queryClient.setQueryData(['isCallerAdmin'], false);
      queryClient.setQueryData(['isCallerTemporaryAdmin'], false);
    }
  }, []); // Run only once on mount
  const [networkInfo, setNetworkInfo] = useState<{ chainId: string; networkName: string } | null>(
    devBypassEnabled ? { chainId: '0x1', networkName: 'Ethereum Mainnet' } : null
  );
  const [icInitMessage, setIcInitMessage] = useState<string | null>(null);
  const [showReconnectionPrompt, setShowReconnectionPrompt] = useState(false);
  
  // Initialize dev bypass on mount
  useEffect(() => {
    if (devBypassEnabled) {
      console.log('🔧 [DEV] Localhost wallet bypass enabled for testing');
      console.log('🔧 [DEV] Mock wallet:', {
        address: walletAddress,
        type: walletType,
        chainId: networkInfo?.chainId
      });
    }
  }, []);

  const { actor, isFetching: actorFetching } = useActor();
  const { identity, loginStatus } = useInternetIdentity();
  const restorationAttempted = useRef(false);
  const pendingSyncRef = useRef<{ address: string; type: WalletType } | null>(null);
  const sessionStateRef = useRef<{ address: string; type: WalletType; timestamp: number } | null>(null);
  const reconnectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const appFocusTimeRef = useRef<number>(Date.now());
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if IC is ready for wallet operations
  // Internet Identity is optional - wallets can connect without it (see syncWalletWithBackend line 464)
  const isICReady = Boolean(
    actor &&
    !actorFetching &&
    loginStatus !== 'initializing' &&
    loginStatus !== 'logging-in'
    // REMOVED: identity && !identity.getPrincipal().isAnonymous()
    // This matches the standard pattern: wallets work independently, II is optional
  );

  // Update initialization message based on state
  useEffect(() => {
    if (!actor || actorFetching) {
      setIcInitMessage('Initializing Internet Computer backend actor...');
    } else if (loginStatus === 'initializing') {
      setIcInitMessage('Initializing Internet Computer identity...');
    } else if (loginStatus === 'logging-in') {
      setIcInitMessage('Authenticating with Internet Identity...');
    } else {
      // Ready for wallet connection - Internet Identity is optional
      setIcInitMessage(null);
    }
  }, [actor, actorFetching, loginStatus]);

  // Enhanced deep link and app-switch detection system with WalletConnect v2 session recovery
  // Debounced to reduce CPU usage - only runs when wallet is actually connected
  useEffect(() => {
    // Early return if no wallet connected - don't set up listeners unnecessarily
    if (!isConnected || !walletAddress || walletType !== 'web3') {
      return;
    }

    let visibilityTimeout: NodeJS.Timeout | null = null;
    
    const handleVisibilityChange = () => {
      // Clear any pending timeout
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
      
      // Debounce the handler - only run after 1000ms of visibility (increased from 500ms)
      visibilityTimeout = setTimeout(() => {
        if (document.visibilityState === 'visible') {
          const timeAway = Date.now() - appFocusTimeRef.current;
          
          // Only check if significant time passed (5+ seconds) and wallet is connected
          // Increased threshold to reduce unnecessary checks
          if (timeAway > 5000 && sessionStateRef.current) {
            if (import.meta.env.DEV) console.log('🔗 [WalletConnect] Potential mobile wallet app switch detected, verifying session...');
            
            // Check wallet session after app switch
            (async () => {
              if (window.ethereum) {
                try {
                  const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                  if (accounts && accounts.length > 0 && accounts[0] === walletAddress) {
                    if (import.meta.env.DEV) console.log('✅ [Wallet] Session still active after app switch');
                    // Don't show toast - session is still active, no action needed
                  } else if (accounts && accounts.length > 0 && accounts[0] !== walletAddress) {
                    if (import.meta.env.DEV) console.log('🔄 [Wallet] Account changed during app switch');
                    setShowReconnectionPrompt(true);
                  } else {
                    if (import.meta.env.DEV) console.log('⚠️ [Wallet] Session lost during app switch');
                    setShowReconnectionPrompt(true);
                  }
                } catch (error) {
                  // Check WalletConnect as fallback
                  const wcProvider = getWalletConnectProvider();
                  if (wcProvider && wcProvider.connected && wcProvider.accounts && wcProvider.accounts.length > 0) {
                    if (wcProvider.accounts[0] !== walletAddress) {
                      setShowReconnectionPrompt(true);
                    }
                  } else {
                    setShowReconnectionPrompt(true);
                  }
                }
              } else {
                // No browser extension, check WalletConnect only
                const wcProvider = getWalletConnectProvider();
                if (!wcProvider || !wcProvider.connected || !wcProvider.accounts || wcProvider.accounts.length === 0 || wcProvider.accounts[0] !== walletAddress) {
                  setShowReconnectionPrompt(true);
                }
              }
            })();
          }

          appFocusTimeRef.current = Date.now();
        } else {
          appFocusTimeRef.current = Date.now();
        }
      }, 1000); // 1000ms debounce to reduce CPU usage
    };

    const handleFocus = () => {
      handleVisibilityChange();
    };

    const handleBlur = () => {
      appFocusTimeRef.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [isConnected, walletAddress, walletType]); // Only run when wallet is connected

  // CRITICAL: Listen for WalletConnect connection events to auto-restore session
  // This handles the case where user approves connection in mobile app and returns to browser
  useEffect(() => {
    const wcProvider = getWalletConnectProvider();
    if (!wcProvider) {
      return;
    }

    // Listen for connect event - this fires when WalletConnect session is established
    const unsubscribeConnect = onWalletConnectConnect(async () => {
      if (import.meta.env.DEV) console.log('🔗 [WalletConnect] Connection event detected - auto-restoring session');
      
      try {
        // Re-fetch provider to get latest state
        const currentProvider = getWalletConnectProvider();
        if (!currentProvider || !currentProvider.accounts || currentProvider.accounts.length === 0) {
          return;
        }

        const address = currentProvider.accounts[0];
        
        // Check current state to avoid duplicate connections
        // Use a small delay to ensure state is stable
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Auto-restore session without requiring user to click "Connect Wallet" again
        // Only proceed if not already connected or if wallet address changed
        const currentAddress = walletAddress;
        const currentlyConnected = isConnected;
        
        if (!currentlyConnected || currentAddress !== address) {
          if (import.meta.env.DEV) console.log('✅ [WalletConnect] Auto-restoring session:', address);
          
          setIsConnecting(false);
          
          // Fetch balances first
          const liveBalances = await fetchLiveBalances(address, 'web3');
          
          // Update state
          setWalletAddress(address);
          setWalletType('web3');
          setBalances(liveBalances);
          setIsConnected(true);
          
          // Update session state
          sessionStateRef.current = {
            address,
            type: 'web3',
            timestamp: Date.now(),
          };
          
          // Save to localStorage
          localStorage.setItem(
            'kawswap_wallet',
            JSON.stringify({
              address,
              type: 'web3',
              balances: liveBalances,
            })
          );
          
          // Sync with backend if IC is ready
          const icReady = isICReady;
          if (icReady) {
            await syncWalletWithBackend(address, 'web3');
            refreshAdminStatus();
          }
          
          // Update network info
          getNetworkInfo().then(setNetworkInfo).catch((error) => {
            if (import.meta.env.DEV) {
              console.warn('⚠️ [Wallet] Network info failed:', error);
            }
          });
          
          toast.success('Wallet Connected', {
            description: `Connected to ${address.slice(0, 6)}...${address.slice(-4)}`,
            duration: 3000,
          });
        }
      } catch (error) {
        console.error('❌ [WalletConnect] Auto-restore failed:', error);
        toast.error('Auto-Connection Failed', {
          description: 'Failed to automatically restore wallet session. Please try connecting manually.',
          duration: 6000,
        });
      }
    });

    return () => {
      if (typeof unsubscribeConnect === 'function') unsubscribeConnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - functions are in scope

  // WalletConnect v2 event listeners
  useEffect(() => {
    if (!isConnected || walletType !== 'web3') {
      return;
    }

    const wcProvider = getWalletConnectProvider();
    if (!wcProvider) {
      return;
    }

    // Listen for account changes
    const unsubscribeAccounts = onWalletConnectAccountsChanged((accounts: string[]) => {
      if (accounts.length === 0) {
        disconnect();
        toast.warning('Wallet Disconnected', {
          description: 'Your wallet session has expired. Please reconnect.',
          duration: 6000,
        });
      } else if (accounts[0] !== walletAddress) {
        setWalletAddress(accounts[0]);
        toast.info('Account Changed', {
          description: 'Connected wallet account has changed.',
          duration: 3000,
        });
      }
    });

    // Listen for chain changes
    const unsubscribeChain = onWalletConnectChainChanged((chainId: number) => {
      getNetworkInfo().then(setNetworkInfo).catch(console.error);
      toast.info('Network Changed', {
        description: `Switched to chain ${chainId}.`,
        duration: 3000,
      });
    });

    // Listen for disconnects
    const unsubscribeDisconnect = onWalletConnectDisconnect(() => {
      disconnect();
    });

    return () => {
      if (typeof unsubscribeAccounts === 'function') unsubscribeAccounts();
      if (typeof unsubscribeChain === 'function') unsubscribeChain();
      if (typeof unsubscribeDisconnect === 'function') unsubscribeDisconnect();
    };
  }, [isConnected, walletType, walletAddress]);

  // Fallback timeout recovery mechanism for stalled sessions - ENHANCED
  useEffect(() => {
    if (isConnecting && walletType === 'web3') {
      // Increased timeout to match the longer WalletConnect connection timeout
      // Mobile users need time to scan QR, open wallet app, navigate, approve, and return
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const fallbackTimeout = isMobile ? 150000 : 100000; // 2.5 minutes mobile, 100 seconds desktop
      
      if (import.meta.env.DEV) console.log(`⏱️ [WalletConnect] Starting connection timeout monitor (${fallbackTimeout / 1000}s)`);
      
      if (reconnectionTimeoutRef.current) {
        clearTimeout(reconnectionTimeoutRef.current);
      }

      reconnectionTimeoutRef.current = setTimeout(() => {
        if (import.meta.env.DEV) console.log('⚠️ [WalletConnect] Connection timeout reached - forcing recovery');
        
        if (isConnecting) {
          if (import.meta.env.DEV) console.log('🔄 [WalletConnect] Connection stalled, attempting automatic recovery...');
          setIsConnecting(false);
          
          if (sessionStateRef.current) {
            if (import.meta.env.DEV) console.log('💾 [WalletConnect] Found pending session, showing reconnection prompt');
            setShowReconnectionPrompt(true);
            toast.warning('Connection Timeout', {
              description: 'Wallet connection timed out. Please confirm reconnection.',
              duration: 10000,
            });
          } else {
            toast.error('Connection Timeout', {
              description: 'The wallet connection timed out. Please try again.',
              duration: 10000,
            });
          }
        }
      }, fallbackTimeout); // Match WalletConnect timeout to avoid premature warnings
    } else {
      if (reconnectionTimeoutRef.current) {
        clearTimeout(reconnectionTimeoutRef.current);
        reconnectionTimeoutRef.current = null;
      }
    }

    return () => {
      if (reconnectionTimeoutRef.current) {
        clearTimeout(reconnectionTimeoutRef.current);
        reconnectionTimeoutRef.current = null;
      }
    };
  }, [isConnecting, walletType]);

  const confirmReconnection = async () => {
    console.log('✅ [WalletConnect] User confirmed reconnection');
    setShowReconnectionPrompt(false);

    if (!sessionStateRef.current) {
      console.log('⚠️ [WalletConnect] No session state found');
      return;
    }

    const { address, type } = sessionStateRef.current;

    try {
      console.log('🔄 [WalletConnect] Attempting to reconnect:', type, address);
      
      if (window.ethereum && type === 'web3') {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        
        if (accounts.length === 0) {
          toast.error('Reconnection Failed', {
            description: 'Wallet is no longer connected. Please connect manually.',
            duration: 8000,
          });
          disconnect();
          return;
        }

        const currentAddress = accounts[0];
        if (currentAddress !== address) {
          console.log('🔄 [WalletConnect] Address changed:', currentAddress);
          setWalletAddress(currentAddress);
          
          sessionStateRef.current = {
            address: currentAddress,
            type,
            timestamp: Date.now(),
          };

          const liveBalances = await fetchLiveBalances(currentAddress, type);
          setBalances(liveBalances);

          localStorage.setItem(
            'kawswap_wallet',
            JSON.stringify({
              address: currentAddress,
              type,
              balances: liveBalances,
            })
          );

          if (isICReady) {
            await syncWalletWithBackend(currentAddress, type);
          }

          toast.success('Wallet Reconnected', {
            description: 'Your wallet has been reconnected successfully.',
            duration: 6000,
          });
        } else {
          await refreshBalances();
          toast.success('Session Restored', {
            description: 'Your wallet session has been restored.',
            duration: 6000,
          });
        }
      }
    } catch (error: any) {
      console.error('❌ [WalletConnect] Reconnection failed:', error);
      toast.error('Reconnection Failed', {
        description: getSafeErrorMessage(error, 'Failed to reconnect wallet. Please try connecting manually.'),
        duration: 8000,
      });
    }
  };

  const dismissReconnectionPrompt = () => {
    console.log('❌ [WalletConnect] User dismissed reconnection prompt');
    setShowReconnectionPrompt(false);
    sessionStateRef.current = null;
  };

  // Optimized balance fetching with caching and parallel requests
  const balanceCache = useRef<Map<string, { balance: Record<string, number>; timestamp: number }>>(new Map());
  const BALANCE_CACHE_TTL = 5000; // 5 seconds cache for instant feel
  
  // Fetch UTXO balances for manually-added addresses (Trust Wallet workaround)
  const fetchManuallyAddedUTXOBalances = async (): Promise<Record<string, number>> => {
    const utxoBalances: Record<string, number> = {};
    
    try {
      // Get manually-added UTXO addresses from localStorage
      const stored = localStorage.getItem('kawswap_utxo_addresses');
      if (!stored) return utxoBalances;
      
      const utxoAddresses: Record<string, string> = JSON.parse(stored);
      
      // Fetch balances for each manually-added address
      const balancePromises = Object.entries(utxoAddresses).map(async ([token, address]) => {
        if (!address || address.trim() === '') return;
        
        try {
          const tokenUpper = token.toUpperCase();
          
          // Use existing Ravencoin balance fetcher
          if (tokenUpper === 'RVN') {
            const { fetchRavencoinBalance } = await import('../lib/walletConnectors');
            const balance = await fetchRavencoinBalance(address.trim());
            if (balance > 0) {
              utxoBalances[tokenUpper] = (utxoBalances[tokenUpper] || 0) + balance;
              if (import.meta.env.DEV) {
                console.log(`✅ [UTXO Balance] ${token}:`, balance, 'from manually-added address');
              }
            }
          } else {
            // For other UTXO chains (BTC, LTC, DOGE, etc.), we'd need to add specific fetchers
            // For now, we'll log that it's not yet supported
            if (import.meta.env.DEV) {
              console.log(`ℹ️ [UTXO Balance] ${token} balance fetching not yet implemented for manually-added addresses`);
            }
          }
        } catch (error) {
          console.warn(`⚠️ [UTXO Balance] Failed to fetch ${token} balance:`, error);
        }
      });
      
      await Promise.all(balancePromises);
    } catch (error) {
      console.warn('⚠️ [UTXO Balance] Failed to fetch manually-added UTXO balances:', error);
    }
    
    return utxoBalances;
  };

  const fetchLiveBalances = async (address: string, type: WalletType): Promise<Record<string, number>> => {
    // Check cache first for instant response
    const cacheKey = `${address}-${type}`;
    const cached = balanceCache.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < BALANCE_CACHE_TTL) {
      // Still merge manually-added UTXO balances (they're not cached)
      const utxoBalances = await fetchManuallyAddedUTXOBalances();
      return { ...cached.balance, ...utxoBalances };
    }
    const liveBalances: Record<string, number> = {};
    console.log(`💰 [Balance] Fetching for ${type}:`, address);

    try {
      if (type === 'icp') {
        console.log('🔄 [Balance] ICP wallet detected, starting balance fetch...');
        console.log('🔄 [Balance] Address/Principal:', address);
        
        // Fetch ICP and chain-key token balances
        console.log('🔄 [Balance] Importing fetchAllICPBalances...');
        const { fetchAllICPBalances } = await import('../lib/icpBalanceFetcher');
        console.log('✅ [Balance] fetchAllICPBalances imported, calling...');
        
        const icpBalances = await fetchAllICPBalances(address);
        console.log('✅ [Balance] fetchAllICPBalances returned:', icpBalances);
        
        Object.assign(liveBalances, icpBalances);
        console.log('✅ [Balance] ICP balances assigned to liveBalances:', liveBalances);
        console.log('✅ [Balance] Balance keys:', Object.keys(liveBalances));
        console.log('✅ [Balance] Full liveBalances object:', liveBalances);
        console.log('✅ [Balance] ICP value:', liveBalances.ICP);
        console.log('✅ [Balance] ckBTC value:', liveBalances.ckBTC);
        console.log('✅ [Balance] ckETH value:', liveBalances.ckETH);
        console.log('✅ [Balance] ckDOGE value:', liveBalances.ckDOGE);
      } else if (type === 'web3') {
        // Fetch native ETH balance and all ERC-20 tokens
        const { fetchNativeBalance, fetchERC20Tokens } = await import('../lib/multiChainRPC');
        
        try {
          // Fetch native ETH balance
          const ethBalance = await fetchNativeBalance('ethereum', address);
          liveBalances.ETH = ethBalance;
          console.log('✅ [Balance] ETH:', ethBalance);
        } catch (error) {
          console.warn('⚠️ [Balance] ETH fetch failed:', error);
          liveBalances.ETH = 0;
        }

        try {
          // Fetch all ERC-20 token balances (USDT, USDC, DAI, WBTC, UNI, LINK, etc.)
          console.log('🔄 [Balance] Fetching ERC-20 tokens...');
          const erc20Tokens = await fetchERC20Tokens('ethereum', address);
          console.log(`✅ [Balance] Found ${erc20Tokens.length} ERC-20 tokens`);
          for (const token of erc20Tokens) {
            if (token.balance > 0) {
              liveBalances[token.symbol] = token.balance;
              console.log(`✅ [Balance] ${token.symbol}:`, token.balance);
            }
          }
        } catch (error) {
          console.warn('⚠️ [Balance] ERC-20 tokens fetch failed:', error);
        }
        
        // Fetch custom token balances (if contract address provided)
        try {
          const { useGetCustomTokenPairs } = await import('../hooks/useQueries');
          // Note: We can't use hooks here, so we'll fetch custom tokens via the backend
          // For now, custom tokens with contract addresses will be fetched via fetchERC20Tokens above
          // This is a limitation - we'd need to pass customTokenPairs as a parameter
          // or fetch them via the backend actor directly
        } catch (error) {
          // Silently fail - custom tokens are optional
          if (import.meta.env.DEV) {
            console.warn('⚠️ [Balance] Custom token balance fetching not available:', error);
          }
        }

        // Initialize other tokens to 0 (for display consistency)
        if (!('BTC' in liveBalances)) liveBalances.BTC = 0;
        if (!('RVN' in liveBalances)) liveBalances.RVN = 0;
        if (!('LTC' in liveBalances)) liveBalances.LTC = 0;
        if (!('DOGE' in liveBalances)) liveBalances.DOGE = 0;
        if (!('USDT' in liveBalances)) liveBalances.USDT = 0;
        if (!('USDC' in liveBalances)) liveBalances.USDC = 0;
        if (!('BNB' in liveBalances)) liveBalances.BNB = 0;
        if (!('ADA' in liveBalances)) liveBalances.ADA = 0;
        if (!('XRP' in liveBalances)) liveBalances.XRP = 0;
        if (!('HYPE' in liveBalances)) liveBalances.HYPE = 0;
        
        // Try to fetch UTXO balances via bip122 (for wallets that support it)
        try {
          const { checkUTXOSupport, getUTXOBalance, getUTXOAddresses } = await import('../lib/utxoWalletSigner');
          
          if (checkUTXOSupport()) {
            console.log('[Balance] Wallet supports bip122, fetching UTXO balances...');
            
            // Get UTXO addresses from wallet
            try {
              const utxoAddresses = await getUTXOAddresses();
              console.log('[Balance] Found UTXO addresses:', utxoAddresses);
              
              // Fetch balances for supported UTXO tokens
              const utxoTokens = ['BTC', 'RVN', 'LTC', 'DOGE', 'BCH', 'ZEC', 'DASH', 'KMD', 'QTUM'];
              const utxoBalancePromises = utxoTokens.map(async (token) => {
                try {
                  // Try to get balance for first address (or use provided address)
                  const balance = await getUTXOBalance(token, utxoAddresses[0] || address);
                  if (balance > 0) {
                    liveBalances[token] = (liveBalances[token] || 0) + balance;
                    console.log(`✅ [Balance] ${token} (via bip122):`, balance);
                  }
                } catch (error) {
                  // Silently fail for individual tokens
                  if (import.meta.env.DEV) {
                    console.warn(`⚠️ [Balance] Failed to fetch ${token} via bip122:`, error);
                  }
                }
              });
              
              await Promise.all(utxoBalancePromises);
            } catch (error) {
              console.warn('[Balance] Could not fetch UTXO addresses via bip122:', error);
            }
          }
        } catch (error) {
          // Non-critical: bip122 balance fetching failed, continue with RPC fallback
          if (import.meta.env.DEV) {
            console.warn('[Balance] bip122 balance fetching not available:', error);
          }
        }
        
        // Fetch manually-added UTXO balances (Trust Wallet workaround - still needed for wallets without bip122)
        const utxoBalances = await fetchManuallyAddedUTXOBalances();
        Object.assign(liveBalances, utxoBalances);
      } else if (type === 'ravencoin') {
        // Fetch native RVN balance and all Ravencoin assets
        // For Ravencoin Core wallet, use the proper RPC connection
        const { fetchNativeBalance, fetchERC20Tokens } = await import('../lib/multiChainRPC');
        
        try {
          // Try using the proper fetchRavencoinBalance first (uses correct RPC config)
          // This ensures we use the same RPC connection that was used to connect the wallet
          const { fetchRavencoinBalance } = await import('../lib/walletConnectors');
          try {
            const rvnBalance = await fetchRavencoinBalance(address);
            liveBalances.RVN = rvnBalance;
            if (import.meta.env.DEV) console.log('✅ [Balance] RVN (via wallet RPC):', rvnBalance);
          } catch (rpcError) {
            // Fallback to multiChainRPC if wallet RPC fails
            const rvnBalance = await fetchNativeBalance('ravencoin', address);
            liveBalances.RVN = rvnBalance;
            if (import.meta.env.DEV) console.log('✅ [Balance] RVN (via multiChainRPC):', rvnBalance);
          }
        } catch (error) {
          console.warn('⚠️ [Balance] RVN fetch failed:', error);
          liveBalances.RVN = 0;
        }

        try {
          // Fetch all Ravencoin assets (fetchERC20Tokens handles Ravencoin internally)
          const ravencoinAssets = await fetchERC20Tokens('ravencoin', address);
          for (const asset of ravencoinAssets) {
            if (asset.balance > 0) {
              liveBalances[asset.symbol] = asset.balance;
              if (import.meta.env.DEV) console.log(`✅ [Balance] ${asset.symbol}:`, asset.balance);
            }
          }
        } catch (error) {
          console.warn('⚠️ [Balance] Ravencoin assets fetch failed:', error);
        }

        // Initialize other tokens to 0 (for display consistency)
        if (!('BTC' in liveBalances)) liveBalances.BTC = 0;
        if (!('ETH' in liveBalances)) liveBalances.ETH = 0;
        if (!('LTC' in liveBalances)) liveBalances.LTC = 0;
        if (!('DOGE' in liveBalances)) liveBalances.DOGE = 0;
        if (!('USDT' in liveBalances)) liveBalances.USDT = 0;
        if (!('USDC' in liveBalances)) liveBalances.USDC = 0;
        if (!('BNB' in liveBalances)) liveBalances.BNB = 0;
        if (!('ADA' in liveBalances)) liveBalances.ADA = 0;
        if (!('XRP' in liveBalances)) liveBalances.XRP = 0;
        if (!('HYPE' in liveBalances)) liveBalances.HYPE = 0;
      } else if (type === 'solana') {
        // Fetch native SOL balance and all SPL tokens
        // For embedded Solana wallets, try wallet connection first
        const { fetchNativeBalance, fetchERC20Tokens } = await import('../lib/multiChainRPC');
        
        try {
          // Try using the wallet connector first (uses wallet's own connection if available)
          const { fetchSolanaBalance } = await import('../lib/solanaWalletConnector');
          try {
            const solBalance = await fetchSolanaBalance(address);
            liveBalances.SOL = solBalance;
            if (import.meta.env.DEV) console.log('✅ [Balance] SOL (via wallet):', solBalance);
          } catch (walletError) {
            // Fallback to multiChainRPC if wallet method fails
            const solBalance = await fetchNativeBalance('solana', address);
            liveBalances.SOL = solBalance;
            if (import.meta.env.DEV) console.log('✅ [Balance] SOL (via RPC):', solBalance);
          }
        } catch (error) {
          console.warn('⚠️ [Balance] SOL fetch failed:', error);
          liveBalances.SOL = 0;
        }

        try {
          // Fetch all SPL tokens (fetchERC20Tokens handles Solana internally)
          const splTokens = await fetchERC20Tokens('solana', address);
          for (const token of splTokens) {
            if (token.balance > 0) {
              // Use symbol if available, otherwise use contract address
              const symbol = token.symbol || token.contractAddress?.slice(0, 6) || 'SPL';
              liveBalances[symbol] = token.balance;
              if (import.meta.env.DEV) console.log(`✅ [Balance] ${symbol}:`, token.balance);
            }
          }
        } catch (error) {
          console.warn('⚠️ [Balance] SPL tokens fetch failed:', error);
        }

        // Initialize wSOL to 0 if not found
        if (!('wSOL' in liveBalances)) liveBalances.wSOL = 0;
      } else if (type === 'xrp') {
        // XRP balance is already fetched in connect function
        // Just ensure it's in the balances object
        try {
          const { fetchXRPBalance } = await import('../lib/walletConnectors');
          const xrpBalance = await fetchXRPBalance(address);
          liveBalances.XRP = xrpBalance;
          if (import.meta.env.DEV) console.log('✅ [Balance] XRP:', xrpBalance);
        } catch (error) {
          console.warn('⚠️ [Balance] XRP fetch failed:', error);
          liveBalances.XRP = 0;
        }
      }
    } catch (error) {
      console.error('❌ [Balance] Fetch failed:', error);
      console.error('❌ [Balance] Error details:', error instanceof Error ? error.message : String(error));
      
      // Return appropriate empty balances based on wallet type
      if (type === 'icp') {
        return {
          ICP: 0,
          ckBTC: 0,
          ckETH: 0,
          ckDOGE: 0,
        };
      } else if (type === 'solana') {
        return {
          SOL: 0,
          wSOL: 0,
        };
      } else if (type === 'xrp') {
        return {
          XRP: 0,
        };
      } else {
        // Default for web3, ravencoin, etc.
        return {
          RVN: 0,
          BTC: 0,
          ETH: 0,
          LTC: 0,
          DOGE: 0,
          USDT: 0,
          USDC: 0,
          BNB: 0,
          ADA: 0,
          XRP: 0,
          HYPE: 0,
        };
      }
    }

    // Cache the result for instant future access
    balanceCache.current.set(cacheKey, { balance: liveBalances, timestamp: Date.now() });
    
    console.log('✅ [Balance] fetchLiveBalances COMPLETE - Returning balances:', liveBalances);
    console.log('✅ [Balance] Return object keys:', Object.keys(liveBalances));
    
    return liveBalances;
  };

  const refreshBalances = async () => {
    if (!walletAddress || !walletType) return;

    console.log('🔄 [Balance] Refreshing...');
    try {
      const liveBalances = await fetchLiveBalances(walletAddress, walletType);
      
      if (import.meta.env.DEV) {
        console.log('✅ [Balance] Refresh - Live balances fetched:', liveBalances);
        console.log('✅ [Balance] Refresh - Balance keys:', Object.keys(liveBalances));
      }
      
      setBalances(liveBalances);
      localStorage.setItem(
        'kawswap_wallet',
        JSON.stringify({
          address: walletAddress,
          type: walletType,
          balances: liveBalances,
        })
      );
      console.log('✅ [Balance] Refresh complete');
      toast.success('Balances Updated', {
        description: 'Your wallet balances have been refreshed.',
        duration: 3000,
      });
    } catch (error) {
      console.error('❌ [Balance] Refresh failed:', error);
      if (import.meta.env.DEV) {
        console.error('Error details:', error);
      }
      toast.error('Balance Refresh Failed', {
        description: 'Failed to refresh wallet balances.',
        duration: 6000,
      });
    }
  };

  const syncWalletWithBackend = async (address: string, type: WalletType): Promise<void> => {
    console.log('🔄 [Wallet] Syncing with backend...');

    if (!isICReady) {
      console.log('⏳ [Wallet] IC not ready yet, queueing sync...');
      pendingSyncRef.current = { address, type };
      return;
    }

    // Backend sync works without Internet Identity - wallet address verification is sufficient
    if (!actor) {
      console.log('ℹ️ [Wallet] Backend actor not available - wallet connected locally only');
      pendingSyncRef.current = { address, type };
      return;
    }

    // Log principal if available, but proceed even if anonymous
    if (identity && !identity.getPrincipal().isAnonymous()) {
      console.log('✅ [Wallet] Principal:', identity.getPrincipal().toString());
    } else {
      console.log('ℹ️ [Wallet] Using anonymous identity - wallet address verification will be used');
    }

    try {
      const backendWalletType = toBackendWalletType(type);

      await actor.connectWallet(backendWalletType, address);
      if (import.meta.env.DEV) console.log('✅ [Wallet] Backend connection successful');

      await actor.syncWalletWithBackend();
      if (import.meta.env.DEV) console.log('✅ [Wallet] Sync complete');

      // Check if this is the admin wallet
      const isAdminAddress = await actor.isAdminWalletAddress(address);
      if (import.meta.env.DEV) console.log('🔍 [Wallet] Admin check result:', { address, isAdminAddress });
      
      // Also check the hardcoded admin address directly
      const ADMIN_WALLET = '0x3877861De1D14efd423BeE7cD047EA193b06DBcc';
      const addressMatches = address.toLowerCase() === ADMIN_WALLET.toLowerCase();
      if (import.meta.env.DEV) console.log('🔍 [Wallet] Direct comparison:', { address: address.toLowerCase(), admin: ADMIN_WALLET.toLowerCase(), matches: addressMatches });

      if (isAdminAddress || addressMatches) {
        if (import.meta.env.DEV) console.log('🔐 [Wallet] Admin wallet detected:', address);
        
        // If admin wallet, register it to get admin access
        try {
          // Generate a message to sign (in production, this should be more secure)
          const message = `KawSwap Admin Verification\nWallet: ${address}\nTimestamp: ${Date.now()}`;
          
          // For Web3 wallets, get signature
          if (window.ethereum && (type === 'web3')) {
            try {
              const signature = await window.ethereum.request({
                method: 'personal_sign',
                params: [message, address],
              });
              
              await actor.registerAdminWallet(address, signature);
              if (import.meta.env.DEV) console.log('✅ [Wallet] Admin wallet registered with signature');
            } catch (sigError: any) {
              // If signature fails, try without signature (backend may allow it)
              if (import.meta.env.DEV) console.warn('⚠️ [Wallet] Signature failed, trying without:', sigError?.message);
              try {
                await actor.registerAdminWallet(address, '');
                if (import.meta.env.DEV) console.log('✅ [Wallet] Admin wallet registered without signature');
              } catch (regError: any) {
                if (import.meta.env.DEV) console.warn('⚠️ [Wallet] Admin registration failed:', regError?.message);
              }
            }
          } else {
            // For non-Web3 wallets, try without signature
            try {
              await actor.registerAdminWallet(address, '');
              if (import.meta.env.DEV) console.log('✅ [Wallet] Admin wallet registered (non-Web3)');
            } catch (regError: any) {
              if (import.meta.env.DEV) console.warn('⚠️ [Wallet] Admin registration failed:', regError?.message);
            }
          }
        } catch (regError: any) {
          if (import.meta.env.DEV) console.warn('⚠️ [Wallet] Admin registration failed (may already be registered):', regError?.message);
        }
        
        // Force immediate admin status refresh - set it immediately
        setAdminStatus(true);
        
        // Invalidate and refetch admin queries immediately
        refreshAdminStatus();
        
        // Also check admin status directly from backend after a short delay
        setTimeout(async () => {
          try {
            const adminStatus = await actor.isCallerAdmin();
            if (import.meta.env.DEV) console.log('🔍 [Wallet] Direct admin status check:', adminStatus);
            if (adminStatus) {
              setAdminStatus(true);
              refreshAdminStatus();
            }
          } catch (err) {
            if (import.meta.env.DEV) console.warn('⚠️ [Wallet] Admin status check failed:', err);
          }
        }, 1000);
        
        toast.success('Admin Wallet Connected', {
          description: 'You now have access to the admin dashboard.',
          duration: 6000,
        });
      } else {
        if (import.meta.env.DEV) console.log('❌ [Wallet] Not admin wallet:', { address, isAdminAddress });
      }

      if (import.meta.env.DEV) console.log('🎉 [Wallet] Backend sync complete');
      pendingSyncRef.current = null;
      refreshAdminStatus();
    } catch (error: any) {
      console.error('❌ [Wallet] Backend sync failed:', error);
      // Don't throw - allow wallet to work locally even if backend sync fails
      console.log('ℹ️ [Wallet] Continuing with local wallet connection');
      pendingSyncRef.current = { address, type };
    }
  };

  const connect = async (type: WalletType) => {
    if (!type) {
      throw new Error('Wallet type is required');
    }

    // Dev bypass: skip actual connection on localhost
    if (devBypassEnabled) {
      console.log('🔧 [DEV] Using wallet bypass - skipping actual connection');
      setIsConnecting(false);
      setIsConnected(true);
      setWalletType(type);
      setWalletAddress('0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb');
      setNetworkInfo({ chainId: '0x1', networkName: 'Ethereum Mainnet' });
      setBalances({ 
        RVN: 1000, 
        BTC: 0.5, 
        ETH: 10, 
        USDT: 5000, 
        USDC: 3000,
        LTC: 50,
        DOGE: 10000
      });
      toast.success('🔧 [DEV] Wallet connected (bypass mode)', {
        description: 'Using mock wallet for localhost testing',
        duration: 3000,
      });
      return;
    }

    if (import.meta.env.DEV) console.log(`🔗 [Wallet] Connecting ${type}...`);

    // IC readiness is optional - allow wallet connection without IC
    if (!isICReady && import.meta.env.DEV) {
      console.log('ℹ️ [Wallet] IC not ready - connecting wallet locally only');
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      let address: string;
      let liveBalances: Record<string, number>;

      switch (type) {
        case 'web3':
          const web3Result = await connectWeb3Wallet();
          address = web3Result.address;
          console.log('🔗 [Wallet] Connected address:', address);

          // Fetch network info in background - don't block connection
          getNetworkInfo()
            .then(setNetworkInfo)
            .catch((netError) => {
              console.warn('⚠️ [Wallet] Network info failed:', netError);
            });

          // Try to connect UTXO provider for NOW wallet and other UTXO-supporting wallets
          // This enables RVN transaction signing via WalletConnect
          try {
            const { connectUTXOProvider, checkUTXOSupport } = await import('../lib/utxoWalletSigner');
            await connectUTXOProvider();
            if (checkUTXOSupport()) {
              console.log('✅ [Wallet] UTXO provider connected - wallet can sign UTXO transactions');
              toast.info('UTXO Support Enabled', {
                description: 'Your wallet supports UTXO chains (BTC, RVN, LTC, DOGE, BCH, ZEC, DASH, KMD, QTUM) - transaction signing enabled',
                duration: 5000,
              });
            }
          } catch (utxoError: any) {
            // Non-critical - wallet might not support UTXO, continue anyway
            console.log('ℹ️ [Wallet] UTXO provider not available (wallet may not support UTXO):', utxoError?.message);
          }

          liveBalances = await fetchLiveBalances(address, 'web3');
          toast.success('Web3 Wallet Connected', {
            description: `Connected to ${address.slice(0, 6)}...${address.slice(-4)}`,
            duration: 6000,
          });
          break;

        case 'ravencoin':
          // Ravencoin Core wallet connection (localhost:8766)
          // Requires Ravencoin Core running with RPC enabled
          const rvnResult = await connectRavencoinCore();
          address = rvnResult.address;

          if (!isValidRavencoinAddress(address)) {
            throw new Error('Invalid Ravencoin wallet address');
          }

          liveBalances = await fetchLiveBalances(address, 'ravencoin');
          toast.success('Ravencoin Wallet Connected', {
            description: `Connected to ${address.slice(0, 6)}...${address.slice(-4)}`,
            duration: 6000,
          });
          break;

        case 'icp':
          const icpResult = await connectICPWallet();
          address = icpResult.principal;

          // Fetch ICP and chain-key token balances
          const { fetchAllICPBalances } = await import('../lib/icpBalanceFetcher');
          liveBalances = await fetchAllICPBalances(address);
          
          // Log balances for debugging
          if (import.meta.env.DEV) {
            console.log('💰 [Wallet] ICP balances fetched:', liveBalances);
            console.log('💰 [Wallet] Balance keys:', Object.keys(liveBalances));
          }
          
          toast.success('ICP Wallet Connected', {
            description: `Principal: ${address.slice(0, 20)}...`,
            duration: 6000,
          });
          break;

        case 'solana':
          const solResult = await connectSolanaWallet();
          address = solResult.address;

          const solBalance = await fetchSolanaBalance(address);
          liveBalances = {
            SOL: solBalance,
            wSOL: 0, // Would need to fetch SPL token balance
          };
          
          toast.success('Solana Wallet Connected', {
            description: `Address: ${address.slice(0, 10)}...${address.slice(-6)}`,
            duration: 6000,
          });
          break;

        case 'xrp':
          const xrpResult = await connectXRPWallet();
          address = xrpResult.address;

          const xrpBalance = await fetchXRPBalance(address);
          liveBalances = {
            XRP: xrpBalance,
          };
          
          toast.success('XRP Wallet Connected', {
            description: `Address: ${address.slice(0, 10)}...${address.slice(-6)}`,
            duration: 6000,
          });
          break;

        default:
          throw new Error('Invalid wallet type');
      }

      setWalletAddress(address);
      setWalletType(type);
      setIsConnected(true);
      
      console.log('✅ [Wallet] Connect - Setting balances:', liveBalances);
      console.log('✅ [Wallet] Connect - Balance keys:', Object.keys(liveBalances));
      console.log('✅ [Wallet] Connect - About to call setBalances');
      
      setBalances(liveBalances);
      
      // Verify balances were set
      setTimeout(() => {
        console.log('✅ [Wallet] Connect - Verifying balances were set in state');
      }, 100);

      sessionStateRef.current = {
        address,
        type,
        timestamp: Date.now(),
      };

      localStorage.setItem(
        'kawswap_wallet',
        JSON.stringify({
          address,
          type,
          balances: liveBalances,
        })
      );

      // Backend sync is optional - wallet works locally even if sync fails
      await syncWalletWithBackend(address, type);
      refreshAdminStatus();
    } catch (error: any) {
      setConnectionError(error.message || 'Failed to connect wallet');
      handleWalletError(error);
      throw error;
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = async () => {
    if (import.meta.env.DEV) console.log('🔌 [Wallet] Disconnecting...');

    // Clear admin status IMMEDIATELY - set query data to false before anything else
    queryClient.setQueryData(['isCallerAdmin'], false);
    queryClient.setQueryData(['isCallerTemporaryAdmin'], false);
    
    if (actor && isConnected && isICReady) {
      actor.disconnectWallet().catch((error) => {
        console.error('⚠️ [Wallet] Backend disconnect failed:', error);
      });
    }

    // Disconnect WalletConnect if connected
    if (walletType === 'web3') {
      try {
        await disconnectWalletConnect();
      } catch (error) {
        console.error('Error disconnecting WalletConnect:', error);
      }
    }
    
    // Disconnect ICP wallet if connected
    if (walletType === 'icp') {
      try {
        await disconnectICPWallet();
      } catch (error) {
        console.error('Error disconnecting ICP wallet:', error);
      }
    }
    
    // Disconnect Solana wallet if connected
    if (walletType === 'solana') {
      try {
        await disconnectSolanaWallet();
      } catch (error) {
        console.error('Error disconnecting Solana wallet:', error);
      }
    }
    
    // Disconnect XRP wallet if connected
    if (walletType === 'xrp') {
      try {
        await disconnectXRPWallet();
      } catch (error) {
        console.error('Error disconnecting XRP wallet:', error);
      }
    }
    
    // Disconnect Monero wallet if connected
    if (walletType === 'monero') {
      try {
        await disconnectMoneroWallet();
      } catch (error) {
        console.error('Error disconnecting Monero wallet:', error);
      }
    }

    // Clear wallet state
    setWalletAddress(null);
    setWalletType(null);
    setIsConnected(false);
    setBalances({});
    setConnectionError(null);
    setNetworkInfo(null);
    pendingSyncRef.current = null;
    sessionStateRef.current = null;
    setShowReconnectionPrompt(false);
    localStorage.removeItem('kawswap_wallet');
    setAdminStatus(false);
    
    // Force immediate refetch to ensure admin status is cleared
    refreshAdminStatus();

    if (import.meta.env.DEV) console.log('✅ [Wallet] Disconnected');
    toast.info('Wallet Disconnected', {
      description: 'Your wallet has been disconnected successfully.',
      duration: 3000,
    });
  };

  // Retry pending sync when IC becomes ready
  useEffect(() => {
    if (isICReady && pendingSyncRef.current) {
      const { address, type } = pendingSyncRef.current;
      console.log('🔄 [Wallet] IC ready, retrying sync...');
      
      syncWalletWithBackend(address, type)
        .then(() => {
          console.log('✅ [Wallet] Deferred sync complete');
        })
        .catch((error) => {
          console.error('❌ [Wallet] Deferred sync failed:', error);
          // Don't show toast on page load - this is a background operation
          // Errors are logged to console for debugging
          // toast.error('Backend Registration Failed', {
          //   description: getSafeErrorMessage(error, 'Backend sync failed. Please try reconnecting your wallet.'),
          //   duration: 8000,
          // });
        });
    }
  }, [isICReady]);

  // CRITICAL: Check if WalletConnect is already connected on mount (before IC is ready)
  // This handles the case where wallet is connected but React state hasn't been restored yet
  useEffect(() => {
    // Only run once on mount, before IC is ready
    if (restorationAttempted.current || isConnected) {
      return;
    }

    // Check if WalletConnect provider exists first, then initialize if needed
    (async () => {
      try {
        // Import WalletConnect utilities
        const { initializeWalletConnect, getWalletConnectProvider } = await import('../lib/walletConnect');
        
        // Check if provider already exists before initializing
        let wcProvider = getWalletConnectProvider();
        if (!wcProvider) {
          // Only initialize if provider doesn't exist
          await initializeWalletConnect();
          wcProvider = getWalletConnectProvider();
        }
        
        // Check if WalletConnect provider is already connected
        if (wcProvider && wcProvider.connected && wcProvider.accounts && wcProvider.accounts.length > 0) {
          const address = wcProvider.accounts[0];
          if (import.meta.env.DEV) console.log('🔗 [Wallet] WalletConnect already connected, restoring state:', address);
          
          restorationAttempted.current = true;
          
          try {
            // Fetch balances
            const liveBalances = await fetchLiveBalances(address, 'web3');
            
            // Update state immediately
            setWalletAddress(address);
            setWalletType('web3');
            setBalances(liveBalances);
            setIsConnected(true);
            
            // Update session state
            sessionStateRef.current = {
              address,
              type: 'web3',
              timestamp: Date.now(),
            };
            
            // Save to localStorage
            localStorage.setItem(
              'kawswap_wallet',
              JSON.stringify({
                address,
                type: 'web3',
                balances: liveBalances,
              })
            );
            
            // Update network info
            getNetworkInfo().then(setNetworkInfo).catch((error) => {
              if (import.meta.env.DEV) {
                console.warn('⚠️ [Wallet] Network info failed:', error);
              }
            });
            
            if (import.meta.env.DEV) console.log('✅ [Wallet] WalletConnect session restored');
          } catch (error) {
            console.error('❌ [Wallet] WalletConnect restore failed:', error);
            restorationAttempted.current = false;
          }
        }
      } catch (error) {
        // Provider initialization failed - that's okay, will restore from localStorage later
        if (import.meta.env.DEV) console.log('⚠️ [Wallet] WalletConnect provider not available on mount');
      }
    })();
  }, []); // Run once on mount

  // Restore wallet from localStorage when IC becomes ready
  useEffect(() => {
    if (!isICReady || restorationAttempted.current) {
      return;
    }

    const stored = localStorage.getItem('kawswap_wallet');
    if (stored) {
      restorationAttempted.current = true;

      (async () => {
        try {
          const { address, type, balances: storedBalances } = JSON.parse(stored);
          if (import.meta.env.DEV) console.log('🔄 [Wallet] Restoring:', type, address);

          // Clear admin status on restoration - will be set after verification
          setAdminStatus(false);
          queryClient.setQueryData(['isCallerAdmin'], false);
          queryClient.setQueryData(['isCallerTemporaryAdmin'], false);

          // For WalletConnect, verify session is actually active before restoring
          if (type === 'web3') {
            let walletVerified = false;
            
            // First, check browser extension (most common case)
            if (window.ethereum) {
              try {
                const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                if (accounts && accounts.length > 0 && accounts[0] === address) {
                  walletVerified = true;
                  if (import.meta.env.DEV) console.log('✅ [Wallet] Browser extension account verified');
                }
              } catch (error) {
                if (import.meta.env.DEV) console.log('⚠️ [Wallet] Cannot verify browser extension account');
              }
            }
            
            // If browser extension not verified, check WalletConnect
            // Note: WalletConnect provider might not be initialized yet, so we check if it exists first
            if (!walletVerified) {
              try {
                const wcProvider = getWalletConnectProvider();
                if (wcProvider && wcProvider.connected && wcProvider.accounts && wcProvider.accounts.length > 0 && wcProvider.accounts[0] === address) {
                  walletVerified = true;
                  if (import.meta.env.DEV) console.log('✅ [Wallet] WalletConnect session verified');
                }
              } catch (error) {
                // WalletConnect provider might not be initialized - that's okay, we'll try to restore anyway
                if (import.meta.env.DEV) console.log('⚠️ [Wallet] WalletConnect provider not available');
              }
            }
            
            // If neither browser extension nor WalletConnect is active, don't restore
            // But be more lenient - if browser extension exists but no accounts, still allow restore
            // (user might need to unlock wallet)
            if (!walletVerified && window.ethereum) {
              // Browser extension exists but no accounts - might just be locked
              // Allow restore but it will fail gracefully if wallet is truly disconnected
              if (import.meta.env.DEV) console.log('⚠️ [Wallet] Browser extension exists but no verified account - attempting restore anyway');
            } else if (!walletVerified) {
              // No browser extension and no WalletConnect - don't restore
              if (import.meta.env.DEV) console.log('⚠️ [Wallet] No wallet connection found, skipping restore');
              localStorage.removeItem('kawswap_wallet');
              restorationAttempted.current = false;
              return;
            }
          }

          // Only set as connected after verification
          setWalletAddress(address);
          setWalletType(type);
          setIsConnected(true);
          setBalances(storedBalances || {});

          sessionStateRef.current = {
            address,
            type,
            timestamp: Date.now(),
          };

          if (type === 'web3' && window.ethereum) {
            getNetworkInfo().then(setNetworkInfo).catch(console.warn);
          }

          console.log('🔄 [Wallet] Restore - About to call fetchLiveBalances...');
          console.log('🔄 [Wallet] Restore - Address:', address);
          console.log('🔄 [Wallet] Restore - Type:', type);
          
          fetchLiveBalances(address, type)
            .then((liveBalances) => {
              console.log('✅ [Wallet] Restore - Live balances fetched:', liveBalances);
              console.log('✅ [Wallet] Restore - Balance keys:', Object.keys(liveBalances));
              console.log('✅ [Wallet] Restore - Setting balances in state...');
              console.log('✅ [Wallet] Restore - About to call setBalances with:', liveBalances);
              
              setBalances(liveBalances);
              
              // Verify balances were set
              setTimeout(() => {
                console.log('✅ [Wallet] Restore - Verifying balances were set in state');
              }, 100);
              
              localStorage.setItem(
                'kawswap_wallet',
                JSON.stringify({
                  address,
                  type,
                  balances: liveBalances,
                })
              );
              console.log('✅ [Wallet] Restore - Balances saved to localStorage');
            })
            .catch((error) => {
              // Log errors for debugging
              console.error('❌ [Wallet] Restore - Balance fetch error:', error);
              console.error('❌ [Wallet] Restore - Error message:', error instanceof Error ? error.message : String(error));
              console.error('❌ [Wallet] Restore - Error stack:', error instanceof Error ? error.stack : 'No stack');
            });

          // Sync with backend - errors are handled silently in syncWalletWithBackend
          await syncWalletWithBackend(address, type).catch((error) => {
            // Already handled in syncWalletWithBackend, just prevent unhandled rejection
            if (import.meta.env.DEV) {
              console.error('Backend sync error during restore:', error);
            }
          });
          
          if (import.meta.env.DEV) console.log('✅ [Wallet] Restoration complete');
          toast.success('Wallet Restored', {
            description: 'Your wallet connection has been restored.',
            duration: 3000,
          });
        } catch (error) {
          console.error('❌ [Wallet] Restoration failed:', error);
          // Don't show toast on page load - this is a background operation
          localStorage.removeItem('kawswap_wallet');
          restorationAttempted.current = false;
        }
      })();
    } else {
      // No stored wallet - ensure admin status is cleared
      setAdminStatus(false);
      queryClient.setQueryData(['isCallerAdmin'], false);
      queryClient.setQueryData(['isCallerTemporaryAdmin'], false);
    }
  }, [isICReady]);

  // Listen for account changes
  useEffect(() => {
    if (walletType === 'web3' && window.ethereum && isICReady) {
      const handleAccountsChanged = async (accounts: string[]) => {
        if (accounts.length === 0) {
          disconnect();
        } else if (accounts[0] !== walletAddress) {
          const newAddress = accounts[0];
          setWalletAddress(newAddress);

          sessionStateRef.current = {
            address: newAddress,
            type: walletType,
            timestamp: Date.now(),
          };

          const liveBalances = await fetchLiveBalances(newAddress, walletType);
          setBalances(liveBalances);

          localStorage.setItem(
            'kawswap_wallet',
            JSON.stringify({
              address: newAddress,
              type: walletType,
              balances: liveBalances,
            })
          );

          try {
            await syncWalletWithBackend(newAddress, walletType);
            toast.info('Account Changed', {
              description: `Switched to ${newAddress.slice(0, 6)}...${newAddress.slice(-4)}`,
              duration: 6000,
            });
          } catch (error) {
            console.error('⚠️ [Wallet] Account change sync failed:', error);
            toast.warning('Account Changed', {
              description: 'Account changed but backend sync failed.',
              duration: 6000,
            });
          }
        }
      };

      const handleChainChanged = () => {
        window.location.reload();
      };

      window.ethereum.on?.('accountsChanged', handleAccountsChanged);
      window.ethereum.on?.('chainChanged', handleChainChanged);

      return () => {
        if (window.ethereum) {
          window.ethereum.removeListener?.('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener?.('chainChanged', handleChainChanged);
        }
      };
    }
  }, [walletType, walletAddress, isICReady]);

  // Memoize context value to prevent unnecessary re-renders of all consumers
  const contextValue = React.useMemo(
    () => ({
      isConnected,
      walletAddress,
      walletType,
      connect,
      disconnect,
      balances,
      isConnecting,
      connectionError,
      networkInfo,
      refreshBalances,
      isICReady,
      icInitMessage,
      showReconnectionPrompt,
      confirmReconnection,
      dismissReconnectionPrompt,
    }),
    [
      isConnected,
      walletAddress,
      walletType,
      connect,
      disconnect,
      balances,
      isConnecting,
      connectionError,
      networkInfo,
      refreshBalances,
      isICReady,
      icInitMessage,
      showReconnectionPrompt,
      confirmReconnection,
      dismissReconnectionPrompt,
    ]
  );

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
}

// Export function to add/manage UTXO addresses for Trust Wallet workaround
export function addUTXOAddress(token: string, address: string): void {
  try {
    const stored = localStorage.getItem('kawswap_utxo_addresses');
    const utxoAddresses: Record<string, string> = stored ? JSON.parse(stored) : {};
    utxoAddresses[token.toUpperCase()] = address.trim();
    localStorage.setItem('kawswap_utxo_addresses', JSON.stringify(utxoAddresses));
    console.log(`✅ [UTXO] Added ${token} address: ${address.substring(0, 10)}...`);
  } catch (error) {
    console.error('❌ [UTXO] Failed to save UTXO address:', error);
  }
}

export function getUTXOAddresses(): Record<string, string> {
  try {
    const stored = localStorage.getItem('kawswap_utxo_addresses');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('❌ [UTXO] Failed to load UTXO addresses:', error);
    return {};
  }
}

export function removeUTXOAddress(token: string): void {
  try {
    const stored = localStorage.getItem('kawswap_utxo_addresses');
    const utxoAddresses: Record<string, string> = stored ? JSON.parse(stored) : {};
    delete utxoAddresses[token.toUpperCase()];
    localStorage.setItem('kawswap_utxo_addresses', JSON.stringify(utxoAddresses));
    console.log(`✅ [UTXO] Removed ${token} address`);
  } catch (error) {
    console.error('❌ [UTXO] Failed to remove UTXO address:', error);
  }
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (context === undefined) {
    // Return safe defaults instead of throwing - allows components to render outside provider
    // This is useful for lazy-loaded components that might render before context is ready
    console.warn('[useWallet] WalletContext not available, returning safe defaults');
    return {
      isConnected: false,
      walletAddress: null,
      walletType: null as WalletType,
      connect: async () => { console.warn('[useWallet] connect() called outside WalletProvider'); },
      disconnect: () => { console.warn('[useWallet] disconnect() called outside WalletProvider'); },
      balances: {},
      isConnecting: false,
      connectionError: null,
      networkInfo: null,
      refreshBalances: async () => { console.warn('[useWallet] refreshBalances() called outside WalletProvider'); },
      isICReady: false,
      icInitMessage: null,
      showReconnectionPrompt: false,
      confirmReconnection: () => { console.warn('[useWallet] confirmReconnection() called outside WalletProvider'); },
      dismissReconnectionPrompt: () => { console.warn('[useWallet] dismissReconnectionPrompt() called outside WalletProvider'); },
    };
  }
  return context;
}
